<template>
    <div class="gap-md col-span-12 grid grid-cols-12 h-full relative">
        <!-- Left sidebar with location menu -->
        <div
            v-if="cascadingLocations.length > 0"
            class="transition-all duration-300"
            :class="{ 'absolute h-full z-10': collapseMenu, 'col-span-3 relative flex justify-between rounded-md': !collapseMenu }"
        >
            <LocationMenu
                v-show="!collapseMenu"
                v-model:selected-ids="selectedLocationIds"
                :locations="cascadingLocations"
                :multiple="true"
                class="pl-lg py-xl w-full"
            />
            <div v-show="!collapseMenu" class="h-full w-2 bg-primary"></div>
            <button
                class="collapse-btn absolute transform transition-transform duration-300"
                :class="{ 'left-5 rotate-180': collapseMenu, '-right-2 lg:-right-5': !collapseMenu }"
                @click="toggleCollapseMenu"
            >
                <i class="i-double-arrow block" />
            </button>
        </div>

        <!-- Main content area -->
        <div
            class="flex flex-col transition-all duration-300"
            :class="cascadingLocations.length > 0 ? (collapseMenu ? 'col-span-12' : 'col-span-9') : 'col-span-12'"
        >
            <div class="gap-md mb-lg flex flex-col px-lg pt-xl">
                <div class="gap-sm relative flex items-start">
                    <div class="flex flex-col justify-between   ">
                        <h1>Graphs</h1>
                        <p class="text-(--text-muted)">{{ currentZoneLabel }}</p>
                    </div>
                    <div class="gap-sm ml-auto flex">
                        <InputSelect
                            :value="selectedAggregate"
                            :label="'Aggregate'"
                            :unique-key="0"
                            :options="aggregateOptions"
                            :disabled="aggregateOptions.length === 1"
                            placeholder="Select"
                            class="w-32"
                            @update="setSelectedAggregate"
                        />

                        
                        <InputSelect
                            :value="dateInterval"
                            :label="'Quick select'"
                            :unique-key="1"
                            :options="[
                                { label: 'Custom', value: undefined },
                                { label: 'Last month', value: 'last-month' },
                                { label: 'Last quarter', value: 'last-quarter' },
                                { label: 'Last half', value: 'last-half' },
                                { label: 'Last year', value: 'last-year' },
                                { label: 'Current month', value: 'current-month' },
                                { label: 'Current quarter', value: 'current-quarter' },
                                { label: 'Current half', value: 'current-half' },
                                { label: 'Current year', value: 'current-year' },
                            ]"
                            placeholder="Select option"
                            class="w-64"
                            @update="setDateInterval"
                        />
                        <div class="gap-xs flex flex-col">
                            <label for="startDate" class="mr-sm">Start Date</label>
                            <div class="input-wrapper flex w-[13rem] items-center">
                                <DatePicker
                                    id="startDate"
                                    v-model="startDate"
                                    :manual-input="true"
                                    date-format="dd.M.yy"
                                    placeholder="Select a start date"
                                    class="datepicker"
                                    show-time
                                    hour-format="24"
                                    :max-date="new Date(Date.now() - 20 * 60 * 1000)"
                                    :class="{
                                        'text-(--text-disabled)': !startDate,
                                    }"
                                />
                                <i class="i-calendar pr-sm mr-sm size-5" />
                            </div>
                        </div>
                        <div class="gap-xs flex flex-col">
                            <label for="time-range" class="mr-sm">End Date</label>
                            <div class="input-wrapper flex w-[13rem] items-center">
                                <DatePicker
                                    id="time-range"
                                    v-model="endDate"
                                    :manual-input="true"
                                    date-format="dd.M.yy"
                                    placeholder="Select an end date"
                                    class="datepicker"
                                    show-time
                                    hour-format="24"
                                    :max-date="
                                        customEnergyDateTimeFilter ? new Date(new Date().setMinutes(0, 0, 0)) : new Date()
                                    "
                                    :class="{
                                        'text-(--text-disabled)': !endDate,
                                    }"
                                />
                                <i class="i-calendar pr-sm mr-sm size-5" />
                            </div>
                        </div>

                        <ExportModal
                            :export-type-options="exportTypeOptions"
                            :aggregate-options="aggregateOptions"
                            :selected-aggregate="selectedAggregate"
                            :zones="zones"
                            :start-date="startDate"
                            :end-date="endDate"
                            :data-type="currentDataType || ''"
                            :exporting-data="exportingData"
                            @export="exportFile"
                        />
                    </div>
                </div>
                <div class="flex justify-between">
                    <div class="gap-md flex w-full flex-col relative">
                        <div v-if="loading" class="absolute inset-0 bg-(--surface-1)/50 z-10 flex items-center justify-center">
                             <i class="i-spinner animate-spin text-3xl" />
                        </div>
                        <OverviewTabs
                            v-if="cascadingLocations.length > 0 && tabs.length > 0"
                            :tabs="tabs"
                            :active-tab="currentDataType || ''"
                            class="w-full"
                            @tab-change="handleTabChange"
                        />
                        <section v-else name="no-locations" class="gap-xs flex w-full flex-col items-center text-center">
                            <i class="i-no-results mb-md h-12 w-12" />
                            <div class="gap-xs flex w-80 flex-col items-center text-center">
                                <h2>Oh, no! No locations</h2>
                                <p>
                                    It looks like you don't have access to any locations. Please ask your manager or IT admin to
                                    update your access
                                </p>
                            </div>
                        </section>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, defineAsyncComponent, markRaw, nextTick } from 'vue';
import OverviewTabs from '@/components/overview-tabs.vue';
import { useOrganizationStore } from '@/stores/organization';
import { DatePicker } from 'primevue';
import LocationMenu from '@/components/admin/infrastructure/location-menu.vue';
import { exportData, formatDateToISOString } from '@/stores/helpers';
import type {
    Tab,
    BMSData,
    graphData,
    Option,
    ExportObject,
    ExportInfo,
    RawBMSDataResponse,
    UUIDLocation,
} from '@/stores/store-interfaces';

import InputSelect from '@/components/support/input-select.vue';
import ExportModal from '@/components/data/export-modal.vue';
import { getAggregateOptions } from './aggregate-options';

const organizationStore = useOrganizationStore();
const usersOrganization = computed(() => organizationStore.usersOrganization);
const user = computed(() => organizationStore.user);
const aggregateOptionsFor = (dataType: string) =>
    getAggregateOptions(dataType, { includeRaw: organizationStore.canViewRawSensorData });
const tabs = ref<Tab[]>([]);
function roundToNearest5(date: Date): Date {
    const ms = 1000 * 60 * 5; // 5 minutes in ms
    return new Date(Math.floor(date.getTime() / ms) * ms);
}
const now = roundToNearest5(new Date());
const startDate = ref<Date>(new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)); // 2 days before
const endDate = ref<Date>(now); // snapped to nearest 5
const aggregateOptions = ref<Option[]>([]);
const selectedAggregate = ref<Option[]>([]);

function setSelectedAggregate(value: Option[]) {
    selectedAggregate.value = value;
}

watch(selectedAggregate, async () => {
    if (loading.value || selectedAggregate.value.length === 0) return;
    loading.value = true;
    await filter(zones.value);
    loading.value = false;
});



const cascadingLocations = ref<UUIDLocation[]>([]);
const zones = ref<string[]>([]);
const selectedLocationIds = ref<string[]>([]);

// Helper function to build zone path from location ID
function getZonePathById(locationId: string, locations: UUIDLocation[]): string | null {
    function searchLocation(loc: UUIDLocation, path: string[]): string | null {
        const currentPath = [...path, loc.name];
        if (loc.id === locationId) {
            return currentPath.join(':');
        }
        if (loc.children) {
            for (const child of loc.children) {
                const result = searchLocation(child, currentPath);
                if (result) return result;
            }
        }
        return null;
    }
    
    for (const location of locations) {
        const result = searchLocation(location, []);
        if (result) return result;
    }
    return null;
}

// Watch for selection changes and update zones
watch(selectedLocationIds, (newIds) => {
    if (newIds.length === 0) {
        zones.value = cascadingLocations.value.length > 0 ? [cascadingLocations.value[0].name] : [];
    } else {
        const zonePaths: string[] = [];
        for (const id of newIds) {
            const path = getZonePathById(id, cascadingLocations.value);
            if (path) zonePaths.push(path);
        }
        zones.value = zonePaths;
    }
    
    // Only fetch data if we have zones AND currentDataType is set
    if (zones.value.length > 0 && !loading.value && currentDataType.value) {
        loading.value = true;
        filter(zones.value).finally(() => {
            loading.value = false;
        });
    }
});

const currentZoneLabel = computed(() => {
    if (zones.value.length === 0) return '';
    if (zones.value.length === 1) return zones.value[0];
    
    // Multiple zones - show all of them separated by comma
    return zones.value.join(', ');
});

const loading = ref<boolean>(false);
const isSettingUp = ref<boolean>(false);

const currentDataType = ref<string>();
// const currentDataType = ref<string>();
// Removed watcher for rawMode reset as logic is now handled in handleTabChange
const customEnergyDateTimeFilter = ref<boolean>(false);

const exportTypeOptions = ref<Option[]>([
    { value: 'CSV', label: 'CSV' },
    { value: 'JSON', label: 'JSON' },
]);
const exportingData = ref<boolean>(false);

const allData = ref<graphData[]>([]);
const dateInterval = ref<Option[]>([]);
const collapseMenu = ref(false);

function setDateInterval(value: Option[]) {
    dateInterval.value = value;
}

function toggleCollapseMenu() {
    collapseMenu.value = !collapseMenu.value;
}

onMounted(() => {
    setup();
});

watch(usersOrganization, () => {
    setup();
});

watch(user, () => {
    setup();
});

// Helper to fetch data based on access level and location
async function fetchData(type: string, site: string, aggregationMode: string) {
    const data = await organizationStore.getHistoricData(
        type,
        site,
        formatDateToISOString(startDate.value!),
        formatDateToISOString(endDate.value!),
        undefined,
        undefined,
        undefined,
        aggregationMode,
    );

    return data;
}



// A tab's `id` is its index here; `handleTabChange` maps it back to the data type.
const GRAPH_TABS: { name: string; label?: string; description: string }[] = [
    {
        name: 'power',
        description:
            'The total IT power currently being consumed by your deployed equipment across the selected location(s) during the chosen time period.',
    },
    {
        name: 'humidity',
        description: 'The average humidity level within the selected location(s) during the chosen time period.',
    },
    {
        name: 'temperature',
        description: 'The average air temperature within the selected location(s) during the chosen time period.',
    },
    {
        name: 'pue',
        label: 'PUE',
        description:
            'The data center energy efficiency, showing how efficiently the facility delivers power to IT equipment, within the selected location(s) during the chosen time period.',
    },
    {
        name: 'energy',
        description: 'The total electrical energy consumed within the selected location(s) during the chosen time period.',
    },
];

// `tabs` is a deep ref, so Vue unwraps nested refs: the ComputedRef stored here is read as a plain
// boolean by consumers (`UnwrapRef<Tab>['loading']`), hence the cast on `loading` below.
function setupTabs() {
    GRAPH_TABS.forEach((graphTab, id) => {
        if (!hasAccess(`stat-${graphTab.name}`)) return;
        tabs.value.push({
            id,
            ...graphTab,
            component: markRaw(defineAsyncComponent(() => import('@/components/data/data-graph.vue'))),
            data: computed(() => {
                return allData.value as graphData[];
            }),
            loading: computed(() => loading.value) as unknown as boolean,
        });
    });
}

const hasAccess = (name: string) => {
    return (
        (organizationStore.currentUserRoles.some((role) => role.name === 'org-admin') &&
            organizationStore.currentOrgRoles.some((role) => role.name === name)) ||
        organizationStore.currentUserRoles.some((role) => role.name === name)
    );
};

async function setup() {
    if (
        isSettingUp.value ||
        usersOrganization.value === undefined ||
        user.value === undefined ||
        cascadingLocations.value.length !== 0
    ) {
        return; // already set up or setting up
    }

    isSettingUp.value = true;

    try {
        const locations = (await organizationStore.getLocations()) as UUIDLocation[];
        if (locations.length === 0) {
            return;
        } else {
            // Flatten regions into a single array of sites
            cascadingLocations.value = locations.flatMap((region: UUIDLocation) => region.children);
            // Initialize with first location selected
            if (cascadingLocations.value.length > 0) {
                selectedLocationIds.value = [cascadingLocations.value[0].id];
            }
        }

        if (hasAccess('stat-power')) {
            currentDataType.value = 'power';
        } else if (hasAccess('stat-temperature')) {
            currentDataType.value = 'temperature';
        } else if (hasAccess('stat-humidity')) {
            currentDataType.value = 'humidity';
        } else if (hasAccess('stat-pue')) {
            currentDataType.value = 'pue';
        } else if (hasAccess('stat-energy')) {
            currentDataType.value = 'energy';
        } else {
            return;
        }

        try {
            // Initial setup
            aggregateOptions.value = aggregateOptionsFor(currentDataType.value!);
            selectedAggregate.value = [aggregateOptions.value[0]]; // Default first option

            // Only fetch initial data if we have zones
            if (zones.value.length > 0) {
                const mode = selectedAggregate.value[0].value;
                const data = await fetchData(currentDataType.value!, zones.value[0], mode);

                // Clear any potential existing data to ensure we start clean
                allData.value.splice(0);

                if (mode === 'raw') {
                    const rawData = data as RawBMSDataResponse;
                    const sensors = [...new Set(rawData.data.map((d) => d.sensorName))];

                    sensors.forEach((sensor) => {
                        // Fetch data for each sensor
                        const sensorData = rawData.data.filter((d) => d.sensorName === sensor);

                        allData.value.push({
                            locationName: `${sensor}`,
                            data: sensorData,
                        });
                    });
                } else {
                    allData.value.push({
                        locationName: zones.value[0],
                        data: data.data,
                    });
                }
            }
        } catch (e) {
            console.error('Error fetching initial data:', e);
        }

        setupTabs();
    } finally {
        isSettingUp.value = false;
    }
}

async function filter(newZones: string[], newDataType?: string) {
    const metric = currentDataType.value as 'power' | 'temperature' | 'humidity' | 'pue' | 'energy';
    const targetType = newDataType ?? metric;
    
    let mode: string;

    if (newDataType) {
        const options = aggregateOptionsFor(newDataType);
        mode = options[0].value;
    } else {
         mode = selectedAggregate.value[0]?.value || aggregateOptionsFor(targetType)[0].value;
    }

    if (newZones.length === 0) {
        const data = await fetchData(targetType, zones.value[0], mode);

        if (mode === 'raw') {
            allData.value.splice(0);
            const rawData = data as RawBMSDataResponse;
            const sensors = [...new Set(rawData.data.map((d) => d.sensorName))];

            sensors.forEach((sensor) => {
                // Fetch data for each sensor
                const sensorData = rawData.data.filter((d) => d.sensorName === sensor);
                allData.value.push({
                    locationName: `${sensor}`,
                    data: sensorData,
                });
            });
        } else {
            allData.value.splice(
                0,
                allData.value.length,
                {
                    locationName: cascadingLocations.value[0].name,
                    data: data.data,
                }
            );
        }
    } else {
        if (zones.value === newZones) {
            // date range or type was updated

            const newData: graphData[] = [];
            for (const zone of zones.value) {
                const tempData = await organizationStore.getHistoricData(
                    targetType,
                    zone.split(':')[0],
                    formatDateToISOString(startDate.value!),
                    formatDateToISOString(endDate.value!),
                    zone,
                    undefined,
                    undefined,
                    mode,
                );

                if (!tempData.data || tempData.data.length === 0) {
                    organizationStore.setToastMessage(`No ${metric} data available for ${zone}`, 'warning');
                    continue;
                }

                if (mode === 'raw') {
                    const rawData = tempData as RawBMSDataResponse;
                    const sensors = [...new Set(rawData.data.map((d) => d.sensorName))];

                    sensors.forEach((sensor) => {
                        // Fetch data for each sensor
                        const sensorData = rawData.data.filter((d) => d.sensorName === sensor);
                        newData.push({
                            locationName: sensor,
                            data: sensorData,
                        });
                    });
                } else {
                    newData.push({
                        locationName: zone,
                        data: tempData.data,
                    });
                }
            }

            allData.value.splice(0, allData.value.length, ...JSON.parse(JSON.stringify(newData)));

            return;
        } else {
            // Create a copy of current zones
            const oldZones = [...zones.value];

            // Determine removed and added zones
            const removedZones = oldZones.filter((zone) => !newZones.includes(zone));

            const addedZones = newZones.filter((zone) => !oldZones.includes(zone));

            // Update zones to reflect the new selection
            zones.value = newZones;

            // Remove data for removed zones
            for (const removedZone of removedZones) {
                const idx = allData.value.findIndex((data) => data.locationName === removedZone);
                if (idx !== -1) {
                    allData.value.splice(idx, 1);
                }
            }
            const newData: graphData[] = [];
            // Fetch and add new data

            if (mode === 'raw') {
                // Clear all data to avoid duplicates when switching to raw
                allData.value.splice(0);

                nextTick();
            }

            for (const zone of mode === 'raw' ? zones.value : addedZones) {
                const tempData = await organizationStore.getHistoricData(
                    targetType,
                    zone.split(':')[0],
                    formatDateToISOString(startDate.value!),
                    formatDateToISOString(endDate.value!),
                    zone,
                    undefined,
                    undefined,
                    mode,
                );
                if (!tempData.data || tempData.data.length === 0) {
                    organizationStore.setToastMessage(`No ${metric} data available for ${zone}`, 'warning');
                } else {
                    await nextTick();
                    if (mode === 'raw') {
                        const rawData = tempData as RawBMSDataResponse;
                        const sensors = [...new Set(rawData.data.map((d) => d.sensorName))];
                        sensors.forEach((sensor) => {
                            // Fetch data for each sensor
                            const sensorData = rawData.data.filter((d) => d.sensorName === sensor);
                            newData.push({
                                locationName: sensor,
                                data: sensorData,
                            });
                        });
                    } else {
                        newData.push({
                            locationName: zone,
                            data: tempData.data,
                        });
                    }
                }
            }

            allData.value.push(...newData);
        }
    }
}

async function handleTabChange(tabId: number) {
    loading.value = true;
    try {
        const newType = GRAPH_TABS[tabId].name;

        // Update options and selection for new type
        aggregateOptions.value = aggregateOptionsFor(newType);
        selectedAggregate.value = [aggregateOptions.value[0]];

        // Fetch data for the NEW type with current zones
        await filter(zones.value, newType);
        currentDataType.value = newType;
    } finally {
        loading.value = false;
    }
}

// Aggregated points (BMSData) carry no `unit`; raw points do. The export reads it optionally.
type ExportPoint = BMSData & { unit?: string };

async function exportFile(exportInfo: ExportInfo) {
    // fetch logic
    exportingData.value = true;
    const localData: { locationName: string; data: ExportPoint[] }[] = [];
    const aggMode = exportInfo.aggregate[0]?.value || aggregateOptions.value[0].value;

    for (const zone of zones.value) {
        const tempData = await organizationStore.getHistoricData(
            currentDataType.value!,
            zone.split(':')[0],
            formatDateToISOString(exportInfo.startDate),
            formatDateToISOString(exportInfo.endDate),
            zone,
            undefined,
            `${exportInfo.interval}${exportInfo.intervalUnit[0].value}`,
            aggMode
        );

        if (aggMode === 'raw') {
            const rawData = tempData as RawBMSDataResponse;
            const sensors = [...new Set(rawData.data.map((d) => d.sensorName))];
            sensors.forEach((sensor) => {
                // Fetch data for each sensor
                const sensorData = rawData.data.filter((d) => d.sensorName === sensor);
                localData.push({
                    locationName: sensor,
                    data: sensorData,
                });
            });
        } else {
            localData.push({
                locationName: zone,
                data: tempData.data,
            });
        }
    }


    // export logic
    const type = exportInfo.exportType.value.toLowerCase() as 'csv' | 'json';
    const downloadName = `${currentDataType.value}-data-${aggMode}`;
    const start = exportInfo.startDate;
    const end = exportInfo.endDate;
    const data = localData;

    let exportObject: ExportObject;

    if (type === 'csv') {
        // --- CSV headers (6 columns total for metadata) ---
        const headerRow = ['Zone', 'Time', 'Value', 'Unit'];

        const allDataRows: (string | number)[][] = [];

        // --- Data rows from all sites ---
        data.forEach((site) => {
            const { locationName, data: points } = site;

            points.forEach((point) => {
                allDataRows.push([locationName, point.time ?? '', point.value ?? '', point.unit ?? '']);
            });
        });

        exportObject = {
            exportType: 'csv',
            fields: allDataRows,
            CSVHeaders: headerRow,
            downloadName,
        };
    } else {
        // --- JSON export ---
        exportObject = {
            exportType: 'json',
            fields: [
                {
                    startDate: start,
                    endDate: end,
                    dataType: currentDataType.value,
                    data,
                },
            ],
            downloadName,
        };
    }

    exportData(exportObject);
    exportingData.value = false;
}

setTimeout(() => {
    watch(
        () => startDate.value,
        async () => {
            if (loading.value) return;

            loading.value = true;
            await filter(zones.value);
            loading.value = false;
        },
    );
    watch(
        () => endDate.value,
        async () => {
            if (loading.value) return;

            loading.value = true;
            await filter(zones.value);
            loading.value = false;
        },
    );
    watch(
        () => dateInterval.value,
        async () => {
            loading.value = true;

            if (dateInterval.value.length === 0) {
                loading.value = false;
                return;
            }

            if (dateInterval.value[0]?.value === undefined) {
                await filter(zones.value);
                loading.value = false;
                return;
            }

            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
            let start: Date;
            let end: Date = today;

            const val = dateInterval.value[0]?.value;

            switch (val) {
                // --- LAST PERIODS ---
                case 'last-month': {
                    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    start = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1, 0, 0, 0, 0);
                    end = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
                    break;
                }

                case 'last-quarter': {
                    const currentQuarter = Math.floor(today.getMonth() / 3); // 0–3
                    const lastQuarter = currentQuarter - 1 >= 0 ? currentQuarter - 1 : 3;
                    const yearOffset = currentQuarter === 0 ? -1 : 0;

                    const startMonth = lastQuarter * 3;
                    const year = today.getFullYear() + yearOffset;

                    start = new Date(year, startMonth, 1, 0, 0, 0, 0);
                    end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
                    break;
                }

                case 'last-half': {
                    const currentHalf = today.getMonth() < 6 ? 0 : 1;
                    const lastHalf = currentHalf === 0 ? 1 : 0;
                    const yearOffset = currentHalf === 0 ? -1 : 0;

                    const startMonth = lastHalf * 6;
                    const year = today.getFullYear() + yearOffset;

                    start = new Date(year, startMonth, 1, 0, 0, 0, 0);
                    end = new Date(year, startMonth + 6, 0, 23, 59, 59, 999);
                    break;
                }

                case 'last-year': {
                    start = new Date(today.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
                    end = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
                    break;
                }

                // --- CURRENT PERIODS ---
                case 'current-month': {
                    start = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);

                    break;
                }

                case 'current-quarter': {
                    const quarter = Math.floor(today.getMonth() / 3);
                    const startMonth = quarter * 3;
                    start = new Date(today.getFullYear(), startMonth, 1, 0, 0, 0, 0);
                    // Current quarter → today (partial)
                    end = today;
                    break;
                }

                case 'current-half': {
                    // ✅ Corrected logic — use 0–5 for first half, 6–11 for second
                    const isFirstHalf = today.getMonth() < 6;
                    const startMonth = isFirstHalf ? 0 : 6;
                    start = new Date(today.getFullYear(), startMonth, 1, 0, 0, 0, 0);
                    // Current half → today (partial)
                    end = today;
                    break;
                }

                case 'current-year': {
                    // ✅ Corrected logic — full year up to today
                    start = new Date(today.getFullYear(), 0, 1, 0, 0, 0, 0);
                    end = today;
                    break;
                }

                default:
                    return;
            }

            startDate.value = start;
            endDate.value = end;
            await filter(zones.value);
            loading.value = false;
        },
    );
}, 300);
</script>

<style scoped>
    @reference '@/assets/styles/default.css';

    .button {
    @apply border-2 border-(--btn-outline-fg) bg-(--surface-1) text-(--link-default);

    i {
        filter: brightness(1) invert(0);
    }

    &:hover {
        @apply bg-(--btn-primary-bg) text-(--text-on-accent);

        i {
            transition: filter 0.3s ease;
            filter: brightness(0) invert(1);
        }
    }
}

button.collapse-btn {
    @apply px-xs py-xs absolute top-3 rounded-sm bg-(--overlay-surface) lg:top-12;

    box-shadow:
        0 4px 6px rgba(0, 0, 0, 0.1),
        0 1px 3px rgba(0, 0, 0, 0.06);
}
</style>